import { openPopup, showPopup, hidePopup, updateScrollLock } from './popup-core.js';
import { showPopupFromJson } from './popup-json-loader.js';

/**
 * Returns the top-most open modal dialog (by highest z-index, then by last in DOM order), if any.
 *
 * @returns {HTMLDialogElement|null}
 */
function getTopmostOpenDialog() {
  const openDialogs = document.querySelectorAll('dialog:modal:not(#js-feedback-dialog)');

  if (openDialogs.length === 0) {
    return null;
  }

  let topmostDialog = null;
  // Start from the lowest possible value, since the z-index can be negative.
  // This prevents having to look if it's set explicitly or not on every iteration.
  let highestZIndex = -Infinity;

  openDialogs.forEach((dialog) => {
    const zIndex = parseInt(window.getComputedStyle(dialog).zIndex) || 0;

    if (zIndex > highestZIndex) {
      highestZIndex = zIndex;
      topmostDialog = dialog;
    }
  });

  return topmostDialog;
}

/**
 * Handle default click behavior for dialog popups (close button and backdrop clicks).
 *
 * @param {MouseEvent} event
 */
function handleDefaultDialogClick(event) {
  // Closed via the explicit close button inside a dialog.
  const closeButton = event.target.closest('.js_close_dialog');
  const dialog = closeButton?.closest('dialog');
  if (dialog?.open) {
    dialog.close();
    return;
  }

  // Backdrop click handling for the top-most open dialog.
  const openDialog = getTopmostOpenDialog();
  if (!openDialog) {
    return;
  }

  // Only consider clicks on/within the open dialog itself (including its backdrop).
  // Clicks inside sibling dialogs/popovers (e.g. the feedback dialog stacked on top)
  // must not be treated as a backdrop click on this one.
  if (event.target.closest('dialog') !== openDialog) {
    return;
  }

  // Prevent closing dialogs on backdrop click if the overlay is clinging.
  const overlay = openDialog.querySelector('.popup_modal');
  if (overlay?.classList.contains('js_popup_modal_clinging') && overlay.contains(event.target)) {
    return;
  }

  // Close if the click happened outside the content of the open dialog.
  if (openDialog.querySelector('.js_popup_box')?.contains(event.target) === false) {
    hidePopup(openDialog.id);
  }
}

/**
 * @param {Event} event
 */
function handleDialogCloseEvent(event) {
  const dialog = event.target;
  if (dialog instanceof HTMLDialogElement) {
    dialog.classList.remove('active');
    updateScrollLock();
  }
}

/**
 * Adds default closing logic to dialog popups.
 * If more specific behaviour is needed, replace the generic
 * class and add the logic to the specific element.
 *
 */
function addDefaultDialogPopupListeners() {
  document.addEventListener('click', handleDefaultDialogClick);
  document.addEventListener('close', handleDialogCloseEvent, { capture: true });
}

export { openPopup, addDefaultDialogPopupListeners };

// TODO Migrate usages to ES modules.
window.showPopup = showPopup;
window.showPopupFromJson = showPopupFromJson;
window.hidePopup = hidePopup;
